import React, { useEffect, useState, useCallback, useRef } from 'react';
import { difference } from 'lodash';
import {
  BrazeEvents,
  getAppboy,
  getContentCard,
  getWindow,
  trackContentCardImpressions,
  UserState,
} from '@surfline/web-common';
import { Box } from '@mui/material';

export interface Card {
  name?: string;
  id?: string;
  extras?: Record<string, unknown> | null;
}

export interface WithCardProps {
  card?: Card;
  cards?: Card[];
  defaultCard?: Card;
  defaultCards?: Card[];
  dataTest?: string;
  segmentProperties?: any;
  className?: string;
  testId?: string;
  name?: string;
  isPremium?: boolean;
  user?: UserState['details'] | null;
  daysToDisplay?: Array<number>;
  isDesktop?: boolean;
  isLink?: boolean;
  warning?: {
    alert?: boolean;
    message?: string;
    type?: string;
  };
  entitlements?: Array<string>;
  accountSharingBannerConfig?: {
    [key: string]: {
      ctaBodyText: string;
      ctaBodyLinkText?: string;
      ctaBodyLinkHref?: string;
      ctaButtonText: string;
      discountCode: string;
      discountUrl: string;
    };
  };
}

interface WithContentCardsProps {
  children: React.ReactElement<WithCardProps>;
}

const isControlCard = (card: Card) => card?.extras?.is_control === 'true';
const DEFAULT_BRAZE_TIMEOUT = 3000; // ms

const WithContentCards: React.FC<WithContentCardsProps> = ({ children }) => {
  const { card, cards, defaultCard, defaultCards } = children.props;

  // Important: Always initialize with defaultCard/defaultCards to prevent blank renders
  const [fetchedCard, setFetchedCard] = useState<Card | undefined>(
    defaultCard || (card?.extras ? card : undefined),
  );
  const [fetchedCards, setFetchedCards] = useState<Card[] | undefined>(defaultCards || cards);

  const [fetched, setFetched] = useState(false);
  const eventListenersAdded = useRef(false);
  // @ts-ignore
  const brazeTimedOut = useRef(!!getWindow()?.document[BrazeEvents.BRAZE_TIMEOUT]);
  const hasRunFetch = useRef(false);
  const mountedRef = useRef(true);

  const fetchCards = useCallback(() => {
    if (hasRunFetch.current || !mountedRef.current) return;
    hasRunFetch.current = true;
    setFetched(true);

    if (card?.name) {
      const contentCard = getContentCard(card.name);

      if (isControlCard(contentCard)) {
        trackContentCardImpressions([contentCard]);
        // Keep using defaultCard if the content card is a control card
        setFetchedCard(defaultCard);
      } else if (defaultCard && contentCard) {
        /*
          Verify all entries in the defaultCard are present in the contentCard from Braze
          If not present then fall back to defaultCard
        */
        const requiredKeys = defaultCard?.extras ? Object.keys(defaultCard.extras) : [];
        const keysPresent = contentCard?.extras ? Object.keys(contentCard.extras) : [];
        const isCardValid = difference(requiredKeys, keysPresent).length === 0;

        // If invalid card, use defaultCard, otherwise use the fetched content card
        setFetchedCard(isCardValid ? contentCard : defaultCard);
      } else {
        // If no defaultCard provided, use the content card (if available) or undefined
        setFetchedCard(contentCard || defaultCard);
      }
    }

    if (cards?.length) {
      const contentCards = cards
        .map((cardItem: Card) => {
          if (!cardItem.name) return null;
          const listCard = getContentCard(cardItem.name);
          if (isControlCard(listCard)) {
            trackContentCardImpressions([listCard]);
            return null;
          }
          return listCard || null;
        })
        .filter(Boolean) as Card[];

      // If no content cards were found, use defaultCards if available
      setFetchedCards(contentCards.length === 0 ? defaultCards : contentCards);
    }
  }, [card, cards, defaultCard, defaultCards]);

  const appboyReady = getAppboy() && getAppboy().getCachedContentCards;

  useEffect(() => {
    mountedRef.current = true;

    let brazeTimer: string | number | NodeJS.Timeout | undefined;

    const fetchCardsHandler = () => {
      if (mountedRef.current) {
        fetchCards();
      }
    };

    const timeoutHandler = () => {
      // @ts-ignore
      window.document[BrazeEvents.BRAZE_TIMEOUT] = true;
      brazeTimedOut.current = true;
      if (mountedRef.current) {
        fetchCards();
      }
    };

    if (!fetched) {
      if (appboyReady || brazeTimedOut.current) {
        fetchCards();
      } else if (!eventListenersAdded.current) {
        window.document.addEventListener(BrazeEvents.BRAZE_LOADED, fetchCardsHandler);
        window.document.addEventListener(BrazeEvents.BRAZE_TIMEOUT, timeoutHandler);
        eventListenersAdded.current = true;

        brazeTimer = setTimeout(() => {
          if (!fetched && mountedRef.current) {
            // If nothing has been fetched after timeout interval since adding the
            // braze event listeners, we'll go ahead with the default value
            fetchCards();
          }
        }, DEFAULT_BRAZE_TIMEOUT);
      }
    }

    return () => {
      mountedRef.current = false;

      if (eventListenersAdded.current) {
        window.document.removeEventListener(BrazeEvents.BRAZE_LOADED, fetchCardsHandler);
        window.document.removeEventListener(BrazeEvents.BRAZE_TIMEOUT, timeoutHandler);
        eventListenersAdded.current = false;
      }

      if (brazeTimer) {
        clearTimeout(brazeTimer);
      }

      // Reset hasRunFetch on unmount to handle React 18 double-mount in development
      hasRunFetch.current = false;
    };
  }, [appboyReady, fetchCards, fetched]);

  // Create a copy of the children with updated props
  const componentWithCard = React.cloneElement(children, {
    ...children.props,
    card: fetchedCard || defaultCard, // Ensure we always pass defaultCard as fallback
    cards: fetchedCards || defaultCards, // Ensure we always pass defaultCards as fallback
  });

  return <Box data-testid="with-content-cards">{componentWithCard}</Box>;
};

interface WithContentCardsHOC {
  <P extends WithCardProps>(Component: React.ComponentType<P>): React.FC<P>;
}

const withContentCards: WithContentCardsHOC = <P extends WithCardProps>(
  Component: React.ComponentType<P>,
) => {
  const WrappedComponent: React.FC<P> = (props: P) => (
    <WithContentCards>
      <Component {...props} />
    </WithContentCards>
  );

  WrappedComponent.displayName = `withContentCards(${
    Component.displayName || Component.name || 'Component'
  })`;

  return WrappedComponent;
};

export default withContentCards;
